/**
 * Hierarchical 12-type seasonal colour classifier.
 *
 * Implements the 6-step process from the system prompt:
 *   1. Undertone  (warm | cool, via b* − a*)
 *   2. Visual traits  (brightness, chroma, contrast)
 *   3. Base season  (Spring / Summer / Autumn / Winter)
 *   4. Subseason  (12 types)
 *   5. Dominant trait  (largest normalised deviation from neutral)
 *   6. Confidence  (0.50 – 0.95)
 *
 * The output `SeasonPrediction` uses the stable subtype IDs from
 * `seasonalPalettes.ts` so that `paletteFromPrediction` resolves the right
 * palette without extra mapping downstream.
 *
 * Label mapping (spec name → product label / ID):
 *   clear spring  → Bright Spring  / spring.bright
 *   warm spring   → Warm Spring    / spring.warm
 *   light spring  → Light Spring   / spring.light
 *   soft summer   → Muted Summer   / summer.muted
 *   cool summer   → Cool Summer    / summer.cool
 *   light summer  → Light Summer   / summer.light
 *   deep autumn   → Dark Autumn    / autumn.dark   (spec "light autumn" → autumn.muted)
 *   warm autumn   → Warm Autumn    / autumn.warm
 *   soft autumn   → Muted Autumn   / autumn.muted
 *   deep winter   → Dark Winter    / winter.dark
 *   cool winter   → Cool Winter    / winter.cool
 *   clear winter  → Bright Winter  / winter.bright
 */

import type { FacialColorFeatures } from './facialColorFeatures';
import type { Season } from '../constants/seasonalPalettes';
import { SUBTYPE_BY_ID } from '../constants/seasonalPalettes';

// ─── Threshold constants (all tunable) ───────────────────────────────────────

/** Skin L* above this value is classified as LIGHT */
const L_LIGHT = 65;
/** Skin L* below this value is classified as DEEP */
const L_DEEP = 45;
/** Skin L* above this threshold is "very light" (drives light subseason) */
const L_VERY_LIGHT = 75;
/** Skin L* below this threshold is "very deep" (drives deep subseason) */
const L_VERY_DEEP = 35;

/** Chroma magnitude above this = CLEAR */
const CHROMA_CLEAR = 20;
/** Chroma magnitude below this = SOFT */
const CHROMA_SOFT = 10;

/** |skin L* − hair L*| above this = HIGH contrast */
const CONTRAST_HIGH = 35;
/** |skin L* − hair L*| below this = LOW contrast */
const CONTRAST_LOW = 20;

/** b* − a* values within ±EPSILON of zero get a tiny warm tie-break */
const UNDERTONE_EPSILON = 1.5;

// Normalisation denominators for dominant-trait computation
const BRIGHTNESS_RANGE = 45; // L* range from neutral midpoint (55)
const CHROMA_RANGE = 20;     // chroma range from neutral midpoint (15)
const UNDERTONE_RANGE = 10;  // undertone_score range from zero

// ─── Output types ─────────────────────────────────────────────────────────────

export type UndertoneValue = 'warm' | 'cool';
export type BrightnessValue = 'light' | 'deep';
export type ChromaValue = 'soft' | 'clear';
export type ContrastValue = 'low' | 'high';

export type DominantTrait = 'light' | 'deep' | 'soft' | 'clear' | 'warm' | 'cool';

/** The full debug output produced by the classifier (strict spec JSON shape) */
export interface HierarchicalClassifierOutput {
  undertone: UndertoneValue;
  undertone_score: number;
  brightness: BrightnessValue;
  chroma: ChromaValue;
  contrast: ContrastValue;
  season: Season;
  /** Spec-level subseason label (e.g. "clear spring") */
  subseason: string;
  confidence: number;
  dominant_trait: DominantTrait;
  explanation: string;
}

/** SeasonPrediction-compatible result ready for `paletteFromPrediction` */
export interface HierarchicalSeasonPrediction {
  season: Season;
  /** Stable SEASON_SUBTYPES id (e.g. "spring.bright") */
  subtype: string;
  /** Product display label (e.g. "Bright Spring") */
  subtypeLabel: string;
  seasonConfidence: number;
  subtypeConfidence: number;
  source: 'hierarchical';
  /** Full debug detail if needed by logging / UI */
  detail: HierarchicalClassifierOutput;
}

// ─── Spec → product label mapping ────────────────────────────────────────────

const SPEC_TO_SUBTYPE_ID: Record<string, string> = {
  'light spring':  'spring.light',
  'warm spring':   'spring.warm',
  'clear spring':  'spring.bright',
  'light summer':  'summer.light',
  'cool summer':   'summer.cool',
  'soft summer':   'summer.muted',
  'deep autumn':   'autumn.dark',
  'warm autumn':   'autumn.warm',
  'soft autumn':   'autumn.muted',
  'deep winter':   'winter.dark',
  'cool winter':   'winter.cool',
  'clear winter':  'winter.bright',
};

// ─── Main classifier ─────────────────────────────────────────────────────────

/**
 * Run the 6-step hierarchical seasonal classifier.
 *
 * When hair data is unavailable (`features.contrast === -1`) contrast-based
 * season decisions fall back to chroma alone, and a penalty of −0.10 is
 * applied to the final confidence value.
 */
export function classifyHierarchical(
  features: FacialColorFeatures
): HierarchicalSeasonPrediction {
  const hairAvailable = features.contrast >= 0;

  // ── Step 1: Undertone ────────────────────────────────────────────────────
  const score = features.undertoneScore;
  const undertone: UndertoneValue =
    score > UNDERTONE_EPSILON || (Math.abs(score) <= UNDERTONE_EPSILON && score >= 0)
      ? 'warm'
      : 'cool';

  // ── Step 2: Visual traits ────────────────────────────────────────────────
  const skinL = features.skin.meanL;

  const brightness: BrightnessValue =
    skinL >= L_LIGHT ? 'light' : skinL <= L_DEEP ? 'deep' : 'light'; // mid → lean light

  const chroma: ChromaValue =
    features.chroma >= CHROMA_CLEAR
      ? 'clear'
      : features.chroma <= CHROMA_SOFT
        ? 'soft'
        : features.chroma >= 15 ? 'clear' : 'soft'; // mid → tie-break by closeness

  const contrastRaw = features.contrast;
  const contrast: ContrastValue = hairAvailable
    ? contrastRaw >= CONTRAST_HIGH
      ? 'high'
      : contrastRaw <= CONTRAST_LOW
        ? 'low'
        : chroma === 'clear' ? 'high' : 'low' // mid → use chroma as proxy
    : chroma === 'clear' ? 'high' : 'low'; // no hair → chroma proxy

  // ── Step 3: Base season ──────────────────────────────────────────────────
  let season: Season;

  if (undertone === 'warm') {
    if (brightness === 'light' && chroma === 'clear') {
      season = 'spring';
    } else if (brightness === 'deep' && chroma === 'soft') {
      season = 'autumn';
    } else {
      season = chroma === 'clear' ? 'spring' : 'autumn';
    }
  } else {
    // cool
    if (contrast === 'high' && chroma === 'clear') {
      season = 'winter';
    } else if (contrast === 'low' && chroma === 'soft') {
      season = 'summer';
    } else {
      season = contrast === 'high' ? 'winter' : 'summer';
    }
  }

  // ── Step 5: Dominant trait ───────────────────────────────────────────────
  // Compute normalised deviation from "neutral" for each dimension.
  const brightnessDev = (skinL - 55) / BRIGHTNESS_RANGE; // + = light, − = deep
  const chromaDev = (features.chroma - 15) / CHROMA_RANGE; // + = clear, − = soft
  const undertoneDev = score / UNDERTONE_RANGE;            // + = warm, − = cool

  const absB = Math.abs(brightnessDev);
  const absC = Math.abs(chromaDev);
  const absU = Math.abs(undertoneDev);

  let dominant_trait: DominantTrait;
  if (absB >= absC && absB >= absU) {
    dominant_trait = brightnessDev >= 0 ? 'light' : 'deep';
  } else if (absC >= absU) {
    dominant_trait = chromaDev >= 0 ? 'clear' : 'soft';
  } else {
    dominant_trait = undertoneDev >= 0 ? 'warm' : 'cool';
  }

  // ── Step 4: Subseason ────────────────────────────────────────────────────
  let specSubseason: string;

  switch (season) {
    case 'spring':
      if (skinL >= L_VERY_LIGHT) {
        specSubseason = 'light spring';
      } else if (dominant_trait === 'warm') {
        specSubseason = 'warm spring';
      } else {
        specSubseason = 'clear spring';
      }
      break;

    case 'summer':
      if (skinL >= L_VERY_LIGHT) {
        specSubseason = 'light summer';
      } else if (dominant_trait === 'cool') {
        specSubseason = 'cool summer';
      } else {
        specSubseason = 'soft summer';
      }
      break;

    case 'autumn':
      if (skinL <= L_VERY_DEEP) {
        specSubseason = 'deep autumn';
      } else if (dominant_trait === 'warm') {
        specSubseason = 'warm autumn';
      } else {
        specSubseason = 'soft autumn';
      }
      break;

    case 'winter':
    default:
      if (skinL <= L_VERY_DEEP) {
        specSubseason = 'deep winter';
      } else if (dominant_trait === 'cool') {
        specSubseason = 'cool winter';
      } else {
        specSubseason = 'clear winter';
      }
      break;
  }

  // ── Step 6: Confidence ───────────────────────────────────────────────────
  // Base: magnitude of undertone signal + biggest trait deviation.
  const undertoneStrength = Math.min(1, Math.abs(score) / 15);
  const traitStrength = Math.min(1, Math.max(absB, absC, absU));
  let confidence = 0.5 + undertoneStrength * 0.25 + traitStrength * 0.20;
  if (!hairAvailable) confidence -= 0.10;
  confidence = Math.max(0.5, Math.min(0.95, confidence));

  // ── Resolve product subtype ──────────────────────────────────────────────
  const subtypeId = SPEC_TO_SUBTYPE_ID[specSubseason] ?? `${season}.light`;
  const subtypeEntry = SUBTYPE_BY_ID[subtypeId];
  const subtypeLabel = subtypeEntry?.label ?? specSubseason;

  const explanation =
    `undertone_score=${score.toFixed(2)} (${undertone}), ` +
    `L*=${skinL.toFixed(1)} (${brightness}), ` +
    `chroma=${features.chroma.toFixed(1)} (${chroma}), ` +
    `contrast=${hairAvailable ? contrastRaw.toFixed(1) : 'n/a'} (${contrast}), ` +
    `dominant=${dominant_trait} → ${specSubseason}`;

  const detail: HierarchicalClassifierOutput = {
    undertone,
    undertone_score: parseFloat(score.toFixed(4)),
    brightness,
    chroma,
    contrast,
    season,
    subseason: specSubseason,
    confidence: parseFloat(confidence.toFixed(4)),
    dominant_trait,
    explanation,
  };

  return {
    season,
    subtype: subtypeId,
    subtypeLabel,
    seasonConfidence: parseFloat(confidence.toFixed(4)),
    subtypeConfidence: parseFloat((confidence - 0.05).toFixed(4)),
    source: 'hierarchical',
    detail,
  };
}
